/**
 * Animation utilities using Framer Motion
 * Implements professional, subtle scroll reveal animations with accessibility support
 */

// Check if user prefers reduced motion
export const prefersReducedMotion = () => {
  if (typeof window === 'undefined') return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
};

// Common animation variants for consistent animations across the site
export const fadeInUp = {
  hidden: { 
    opacity: 0, 
    y: 20 
  },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: {
      duration: 0.6,
      ease: [0.25, 0.1, 0.25, 1] // Custom cubic-bezier for smooth easing
    }
  }
};

export const fadeIn = {
  hidden: { 
    opacity: 0 
  },
  visible: { 
    opacity: 1,
    transition: {
      duration: 0.5,
      ease: 'easeOut'
    }
  }
};

export const fadeInLeft = {
  hidden: { 
    opacity: 0, 
    x: -30 
  },
  visible: { 
    opacity: 1, 
    x: 0,
    transition: {
      duration: 0.6,
      ease: [0.25, 0.1, 0.25, 1]
    }
  }
};

export const fadeInRight = {
  hidden: { 
    opacity: 0, 
    x: 30 
  },
  visible: { 
    opacity: 1, 
    x: 0,
    transition: {
      duration: 0.6,
      ease: [0.25, 0.1, 0.25, 1]
    }
  }
};

// Stagger children animation - useful for lists of cards
export const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.1
    }
  }
};

export const staggerItem = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: [0.25, 0.1, 0.25, 1]
    }
  }
};

// Scale animation for hover effects
export const scaleOnHover = {
  scale: 1.02,
  transition: {
    duration: 0.2,
    ease: 'easeOut'
  }
};

// Default viewport options for scroll animations
// More conservative settings to ensure animations trigger only during actual scrolling
export const defaultViewport = {
  once: true, // Animate only once when element comes into view
  margin: "0px", // No margin - animate when element enters viewport
  amount: 0.25 // Trigger when 25% of element is visible
};

// Get animation props for a motion component
// Returns no animation if user prefers reduced motion
export const getAnimationProps = (variant = fadeInUp, viewport = defaultViewport) => {
  if (prefersReducedMotion()) {
    return {
      initial: "visible",
      animate: "visible",
      variants: {
        visible: { opacity: 1, y: 0, x: 0 }
      }
    };
  }

  return {
    initial: "hidden",
    whileInView: "visible",
    viewport,
    variants: variant
  };
};

// Get animation props for elements visible on page load (no scroll trigger)
// Use this for page headers and above-the-fold content
export const getInitialAnimationProps = (variant = fadeInUp, delay = 0) => {
  if (prefersReducedMotion()) {
    return {
      initial: "visible",
      animate: "visible",
      variants: {
        visible: { opacity: 1, y: 0, x: 0 }
      }
    };
  }

  return {
    initial: "hidden",
    animate: "visible",
    variants: variant,
    transition: { 
      delay,
      ...variant.visible.transition
    }
  };
};

// Get stagger animation props for content visible on page load
// Use this for card grids that appear above the fold
export const getInitialStaggerProps = (delay = 0.3) => {
  if (prefersReducedMotion()) {
    return {
      initial: "visible",
      animate: "visible",
      variants: {
        visible: { opacity: 1 }
      }
    };
  }

  return {
    initial: "hidden",
    animate: "visible",
    variants: staggerContainer,
    transition: {
      delayChildren: delay,
      staggerChildren: 0.1
    }
  };
};
